import L from 'leaflet';

const BASE_HULL_POINTS = [
  { x: 0, y: -27 },
  { x: 9, y: -9 },
  { x: 9, y: 24 },
  { x: -9, y: 24 },
  { x: -9, y: -9 }
];

/**
 * Draws a ship hull on the provided 2D canvas context.
 * Accepts map coordinates, rotation, scaling and color customisation so the same
 * silhouette can be reused across different maps.
 *
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D rendering context.
 * @param {Object} [options]
 * @param {number} [options.x=0] - Canvas x coordinate where the ship center should be placed.
 * @param {number} [options.y=0] - Canvas y coordinate where the ship center should be placed.
 * @param {number} [options.headingDeg=0] - Ship heading in degrees, 0 meaning north.
 * @param {number} [options.scale=1] - Optional scale multiplier applied to the base silhouette.
 * @param {string|null} [options.fillColor='#1d4ed8'] - Fill color for the hull. Use null to skip fill.
 * @param {string|null} [options.strokeColor='#bfdbfe'] - Stroke color for the hull outline. Use null to skip stroke.
 * @param {number} [options.strokeWidth=2.2] - Outline width in canvas units (affected by scale).
 * @param {number} [options.alpha=0.85] - Global alpha while drawing the hull.
 */
export function drawShipIcon(ctx, {
  x = 0,
  y = 0,
  headingDeg = 0,
  scale = 1,
  fillColor = '#1d4ed8',
  strokeColor = '#bfdbfe',
  strokeWidth = 2.2,
  alpha = 0.85
} = {}) {
  if (!ctx) {
    return;
  }

  const rad = headingDeg * Math.PI / 180;
  const scaledPoints = BASE_HULL_POINTS.map(point => ({
    x: point.x * scale,
    y: point.y * scale
  }));

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rad);

  const previousAlpha = ctx.globalAlpha;
  ctx.globalAlpha = alpha;

  ctx.beginPath();
  scaledPoints.forEach((point, index) => {
    if (index === 0) {
      ctx.moveTo(point.x, point.y);
    } else {
      ctx.lineTo(point.x, point.y);
    }
  });
  ctx.closePath();

  if (fillColor) {
    ctx.fillStyle = fillColor;
    ctx.fill();
  }
  if (strokeColor) {
    ctx.lineWidth = strokeWidth * scale;
    ctx.strokeStyle = strokeColor;
    ctx.stroke();
  }

  ctx.globalAlpha = previousAlpha;
  ctx.restore();
}

// Cache for rendered ship icons - keyed by visual parameters (not rotation)
const iconCache = new Map();
const MAX_CACHE_SIZE = 500; // Protection against memory leaks

/**
 * Generates a cache key for ship icon parameters.
 * Note: headingDeg is NOT included - rotation is applied via CSS transform.
 */
function getCacheKey({ width, height, scale, fillColor, strokeColor, strokeWidth, alpha }) {
  return `${width}x${height}_s${scale}_f${fillColor}_st${strokeColor}_w${strokeWidth}_a${alpha}`;
}

/**
 * Renders ship icon to data URL with caching.
 * Icons with identical visual parameters (except rotation) are reused.
 * Rotation is applied separately via CSS transform for better performance.
 */
function renderShipIconToDataUrl({
  width,
  height,
  scale,
  fillColor,
  strokeColor,
  strokeWidth,
  alpha,
}) {
  if (typeof document === 'undefined') {
    return null;
  }

  const cacheKey = getCacheKey({ width, height, scale, fillColor, strokeColor, strokeWidth, alpha });
  
  // Return cached version if exists
  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey);
  }

  // Render new icon
  const canvas = document.createElement('canvas');
  const ratio = window.devicePixelRatio || 1;
  canvas.width = width * ratio;
  canvas.height = height * ratio;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    return null;
  }

  ctx.scale(ratio, ratio);
  drawShipIcon(ctx, {
    x: width / 2,
    y: height / 2,
    headingDeg: 0, // Always 0 in cache - rotation applied via CSS
    scale,
    fillColor,
    strokeColor,
    strokeWidth,
    alpha,
  });

  const dataUrl = canvas.toDataURL('image/png');

  // Cache with size limit
  if (iconCache.size >= MAX_CACHE_SIZE) {
    // Remove oldest entry (first in Map)
    const firstKey = iconCache.keys().next().value;
    iconCache.delete(firstKey);
  }
  iconCache.set(cacheKey, dataUrl);

  return dataUrl;
}

/**
 * Creates a Leaflet divIcon for a ship with optimized caching.
 * Icons with same colors/sizes are reused from cache, rotation is applied via CSS transform.
 * This provides significant performance improvement when rendering 100+ ships.
 * 
 * @param {Object} options
 * @param {number} [options.width=28] - Icon width in pixels
 * @param {number} [options.height=56] - Icon height in pixels
 * @param {number} [options.headingDeg=0] - Ship heading in degrees (applied via CSS transform)
 * @param {number} [options.scale=0.7] - Scale multiplier for ship silhouette
 * @param {string} [options.fillColor='#1d4ed8'] - Fill color
 * @param {string} [options.strokeColor='#bfdbfe'] - Stroke color
 * @param {number} [options.strokeWidth=2.2] - Stroke width
 * @param {number} [options.alpha=0.85] - Global alpha
 * @param {string} [options.className='ship-icon'] - CSS class for the icon
 * @param {string} [options.alt='Ship'] - Alt text for the image
 * @param {Array<number>} [options.anchor] - Icon anchor point [x, y]
 * @param {Object} [options.extraStyles={}] - Extra CSS styles for the image
 */
export function createShipIcon({
  width = 28,
  height = 56,
  headingDeg = 0,
  scale = 0.7,
  fillColor = '#1d4ed8',
  strokeColor = '#bfdbfe',
  strokeWidth = 2.2,
  alpha = 0.85,
  className = 'ship-icon',
  alt = 'Ship',
  anchor,
  extraStyles = {},
} = {}) {
  const dataUrl = renderShipIconToDataUrl({
    width,
    height,
    scale,
    fillColor,
    strokeColor,
    strokeWidth,
    alpha,
  });

  const iconAnchor = Array.isArray(anchor) ? anchor : [width / 2, height / 2];

  if (!dataUrl) {
    return L.divIcon({
      className,
      iconSize: [width, height],
      iconAnchor,
    });
  }

  // Apply rotation via CSS transform for better performance
  const styles = {
    transform: `rotate(${headingDeg}deg)`,
    'transform-origin': '50% 50%',
    ...extraStyles,
  };

  const styleStr = Object.entries(styles)
    .map(([key, value]) => `${key}: ${value}`)
    .join('; ');

  return L.divIcon({
    className,
    html: `<img src="${dataUrl}" width="${width}" height="${height}" alt="${alt}" style="${styleStr}" />`,
    iconSize: [width, height],
    iconAnchor,
  });
}

/**
 * Clears the icon cache. Useful for memory management or when switching themes.
 */
export function clearShipIconCache() {
  iconCache.clear();
}

export { BASE_HULL_POINTS };
